import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutTemplate } from 'signup-components/components/LayoutTemplate/LayoutTemplate';
import styled from 'styled-components';
import { getCurrentStepNumbering } from '../flow-management/stepNumberingSelectors';
import { useCanGoBack } from '../hooks/useCanGoBack';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { getLoginUrl } from '../http-login/getLoginUrl';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getHaveAnAccountMessage } from '../step-landing-page/HaveAnAccountLink';
import { getLoginRedirectUrl } from '../step-landing-page/getLoginRedirectUrl';
import { goPrevStep } from '../store/flow/action-creators';
import { useTracker } from '../tracking/useTracker';
import { getSpecificLogoConfig } from '../views-components/HubSpotLogo';
import { Step } from '../views/Step';
import { getStepConfig } from '../views/getStepConfig';
import { AppMessagingComponent } from './AppMessagingComponent';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { AppFooterComponent } from './AppFooterComponent';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { useMessage } from '../messaging/useMessage';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
export const MessagingAboveStepContentContainer = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__MessagingAboveStepContentContainer",
  componentId: "sc-2v1f13-0"
})(["display:flex;justify-content:center;"]);
export const LayoutTemplateViewContainer = (_ref) => {
  let {
    StepViewComponent,
    showProgressIndicator = true,
    showLogo = true,
    showAccountLink = true
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["StepViewComponent", "showProgressIndicator", "showLogo", "showAccountLink"]);

  const {
    isActive: isInstanceOfSignupEmbedder,
    isEmbedded
  } = useSignupEmbedder();
  const {
    currentStep
  } = useCurrentStep();
  const loginRedirectUrl = getLoginRedirectUrl();
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const dispatch = useDispatch();
  const {
    currentStep: currentStepNumber,
    totalSteps
  } = useSelector(getCurrentStepNumbering);
  const {
    component
  } = useMessage();
  const Tracker = useTracker();
  const {
    isMobile
  } = useResponsiveness();
  const {
    url,
    altText
  } = getSpecificLogoConfig(isMobile);
  const stepConfig = getStepConfig(currentStep);
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    layoutType,
    waveType
  } = useLayoutTemplate();
  const alignCentredStepAtTheTop = !isEmbedded && stepConfig && stepConfig.alignCentredStepAtTheTop;
  const {
    isAssignedToSpecificVariant: isAssignedToSpecificVariantACQ134
  } = useExperiment(ExperimentKeys.ACQ0134, TrackWhen.never);
  const shouldShowBackLink = Boolean(canGoBack && completedStep);

  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };

  const onAccountLinkClick = () => {
    Tracker.signupInteraction('click-login-have-an-account', {}, true);
    Tracker.redirect({
      url: 'https://app.hubspot.com/login'
    });
  };

  const onLogoClick = () => {
    Tracker.signupInteraction('click-hubspot-company-logo', {
      currentStep
    }, true);
  };

  const renderMessagingAboveStepContent = () => {
    if (showMessagingAboveContent) {
      return /*#__PURE__*/_jsx(MessagingAboveStepContentContainer, {
        children: /*#__PURE__*/_jsx(AppMessagingComponent, {})
      });
    }

    return null;
  };

  const shouldShowAccountLinkByStep = isAssignedToSpecificVariantACQ134(ExperimentParameterGroup.Variant_B) ? currentStep === Step.Industry : currentStep === Step.LandingPage;
  const shouldShowHaveAnAccountLink = showAccountLink ? shouldShowAccountLinkByStep : false;
  return /*#__PURE__*/_jsx("div", Object.assign({}, props, {
    children: /*#__PURE__*/_jsxs(LayoutTemplate, {
      accountLinkHref: getLoginUrl({
        redirectUrl: loginRedirectUrl
      }),
      accountLinkMessage: getHaveAnAccountMessage(),
      alignCentredStepAtTheTop: alignCentredStepAtTheTop,
      currentStepNumber: currentStepNumber,
      layoutType: layoutType,
      logoAltText: altText,
      logoImageSrc: url,
      messagingContent: component && component(),
      shouldHalveProgressBar: getFlowConfig().shouldHalveProgressBar,
      showBackLink: shouldShowBackLink,
      showHaveAnAccountLink: shouldShowHaveAnAccountLink,
      showLogo: showLogo,
      showManageCookiesLink: true,
      showProgressIndicator: showProgressIndicator,
      totalSteps: totalSteps,
      waveType: waveType,
      onAccountLinkClick: onAccountLinkClick,
      onBackLinkClick: onBackLinkClick,
      onLogoClick: onLogoClick,
      children: [renderMessagingAboveStepContent(), /*#__PURE__*/_jsxs(Suspense, {
        fallback: /*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true,
          size: "extra-small"
        }),
        children: [/*#__PURE__*/_jsx(StepViewComponent, {}), isInstanceOfSignupEmbedder && /*#__PURE__*/_jsx(AppFooterComponent, {})]
      })]
    })
  }));
};